// ─────────────────────────────────────────────────────────────────────
// Tenon · TaskDetailPeek — extra pickers (milestone, decision, FS bridge).
//
// Port of tenon-ds/patterns/task-detail-pickers-extra.jsx → TypeScript.
//
// Exports:
//   MilestoneStatusPicker, DeciderPicker, LocationPicker, RoomPicker,
//   LinkedSpecPicker, LinkedEstimateLineItemPicker, DependencyPicker, DependencyRow,
//   DecisionOptionsEditor, DecisionOptionCard,
//   FSBridgeBlock, FromSpecMaterialRow,
//   DecisionStatusPicker, SelectionModePicker, CloseModePicker, SubKindPicker,
//   DepTypeBadge, LeadLagInput, SwatchStack,
//   + data-type interfaces
// ─────────────────────────────────────────────────────────────────────

import React from 'react'
import { Icon } from '@/design-system/primitives'
import { formatDateWithOptions } from '../../utils/dateUtils'
import { Popover, DropdownMenu } from '@/design-system/primitives'
import type { DropdownMenuItem } from '@/design-system/primitives'
import { fsRoomsApi } from '@/lib/api'
import { formatCurrency } from '@/utils/formatCurrency'

import { authedFetch } from '@/lib/api';
// ─── Exported data interfaces ─────────────────────────────────────────

export interface LocationOption {
  id: string
  name: string
  parentId?: string | null
  projectId?: string
  isProject?: boolean
  hint?: string
}

export interface DepItem {
  id: string
  name: string
  /** 'phase' — a schedule_type='phase' app_tasks row, sequenced like a task. See #2801. */
  type: 'task' | 'spec' | 'permit_milestone' | 'phase'
  status?: string
  /** PERMITS-REDESIGN-SURFACE-SWEEP — set when type === 'permit_milestone' */
  permitStage?: string
  /** TASK-DETAIL-PEEK-DEP-HOVER-PREVIEW — optional hover-card extras */
  vendor?: string    // spec vendor (shown in hover preview for spec deps)
  dueDate?: string   // ISO date string (shown in hover preview)
  /** Project context for cross-project catalogues such as a location Kiosk. */
  project?: string
  /** BUG-DEP-PICKER-DISAMBIG — phase/room, so same-named tasks in different
   *  phases/rooms are distinguishable in the picker and the selected chip. */
  phase?: string
  room?: string
}

export interface Dependency {
  id: string
  name: string
  type: 'task' | 'spec' | 'permit_milestone' | 'phase'
  depType: 'FS' | 'SS' | 'FF' | 'SF'
  lag: number
  status?: string
  /** PERMITS-REDESIGN-SURFACE-SWEEP — set when type === 'permit_milestone' */
  permitStage?: string
  /** The dependency row's own id (app_task_dependencies.id) — set by
   *  useTaskPeekDependencies, used to target the delete-dependency endpoint. */
  _depId?: number
}

export interface SpecOption {
  id: string
  name: string
  vendor?: string
  specType?: string
  status?: string
  allowance?: number
  selectedPrice?: number
  swatches?: string[]
  /** Legacy field name used in demo data */
  selected?: number
  /** Legacy field — room/location label */
  room?: string
  placeholder?: boolean
}

export interface DecisionOption {
  id?: string
  title?: string
  brand?: string | null
  sku?: string | null
  price?: number | null
  quantity?: number | null
  unit?: string | null
  productUrl?: string | null
  specSheetUrl?: string | null
  imageUrl?: string | null
  notes?: string | null
  isBaseline?: boolean
  proposedByClient?: boolean
}

// ─── Internal team / org option ──────────────────────────────────────

export interface TeamOption {
  id: string
  name: string
  role?: string
  avatar?: string
}

// ─── Static data constants ────────────────────────────────────────────

const MILESTONE_STATUS = [
  { id: 'pending', label: 'Pending', dot: 'var(--status-not-started-dot)' },
  { id: 'hit',     label: 'Hit',     dot: 'var(--status-completed-dot)' },
  { id: 'missed',  label: 'Missed',  dot: 'var(--st-error)' },
]

const DECISION_STATUS = [
  { id: 'draft',    label: 'Draft',    dot: 'var(--text-muted)' },
  { id: 'pending',  label: 'Pending',  dot: 'var(--st-warning)' },
  { id: 'approved', label: 'Approved', dot: 'var(--st-success, #22A06B)' },
  { id: 'rejected', label: 'Rejected', dot: 'var(--st-error)' },
  { id: 'revised',  label: 'Revised',  dot: 'var(--brand-gold)' },
]

const DECISION_SUB_KINDS = [
  { id: 'selection', label: 'Selection', hint: 'Pick from N options' },
  { id: 'scope',     label: 'Scope',     hint: 'Locked spec, no options' },
]

const SELECTION_MODES = [
  { id: 'single',   label: 'Single', hint: 'Pick exactly one' },
  { id: 'multiple', label: 'Multi',  hint: 'Pick a combination' },
]

const CLOSE_MODES = [
  { id: 'manual',             label: 'Manual',           hint: 'GC closes' },
  { id: 'on_first_selection', label: 'On first selection', hint: 'Closes when client picks' },
  { id: 'on_deadline',        label: 'On deadline',      hint: 'Auto-closes at deadline' },
]

const DEP_TYPES = [
  { id: 'FS', label: 'FS', hint: 'Finish → Start',
    tip: 'Predecessor must finish before this can start (default).' },
  { id: 'SS', label: 'SS', hint: 'Start → Start',
    tip: 'Both start together (with optional lag).' },
  { id: 'FF', label: 'FF', hint: 'Finish → Finish',
    tip: 'Both finish together (with optional lag).' },
  { id: 'SF', label: 'SF', hint: 'Start → Finish',
    tip: 'Predecessor must start before this finishes — rare.' },
]

const SPEC_TYPE_TINT: Record<string, string> = {
  tile: 'var(--status-in-progress-dot, #7C5CFA)',
  cabinetry: 'color-mix(in srgb, var(--st-warning) 80%, var(--text-muted))',
  fixture: 'var(--status-in-review-dot, #1F8FFF)',
  appliance: 'var(--st-warning)',
  plumbing: 'var(--status-completed-dot, #22A06B)',
  hardware: 'var(--text-muted)',
  paint: 'var(--st-error)',
  flooring: 'var(--status-completed-dot, #22A06B)',
}

const SPEC_STATUS_DOT: Record<string, string> = {
  draft:    'var(--text-muted)',
  pending:  'var(--st-warning)',
  approved: 'var(--st-success, #22A06B)',
  rejected: 'var(--st-error)',
  revised:  'var(--brand-gold)',
}

// ─── Internal shared atoms ────────────────────────────────────────────

interface PickerTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  dot?: string | null
  label?: string
  placeholder?: boolean
  /** Linear/Height-style compact property row — see task-detail-pickers.tsx
   *  PickerTrigger for the canonical doc comment. */
  compact?: boolean
}

// `...rest` MUST be forwarded to the <button>: <Popover> opens by cloning the
// trigger element with an injected onClick (+ aria-*). Without the spread,
// PickerTrigger dropped that onClick and the dropdown never opened (the
// Dependencies picker — and any picker using this trigger in element form).
function PickerTrigger({ dot, label, placeholder, style = {}, compact = false, ...rest }: PickerTriggerProps) {
  const [hover, setHover] = React.useState(false)
  const boxedStyle: React.CSSProperties = {
    width: '100%', textAlign: 'left',
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 12px', minHeight: 40,
    background: 'var(--bg-elevated)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--r-sm)', cursor: 'pointer',
    fontFamily: 'var(--font-sans)', fontSize: 13.5, fontWeight: 600,
    color: placeholder ? 'var(--text-dim)' : 'var(--text-primary)',
    transition: 'border-color var(--dur-fast) var(--ease-out)',
  }
  const compactStyle: React.CSSProperties = {
    width: '100%', textAlign: 'left',
    display: 'flex', alignItems: 'center', gap: 7,
    padding: '0 6px', minHeight: 30,
    background: 'transparent', border: 'none',
    borderRadius: 6, cursor: 'pointer',
    fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500,
    color: placeholder ? 'var(--text-dim)' : 'var(--text-primary)',
  }
  return (
    <button
      type="button"
      {...rest}
      style={{ ...(compact ? compactStyle : boxedStyle), ...style }}
      onMouseEnter={(e) => { compact ? setHover(true) : (e.currentTarget.style.borderColor = 'var(--border-strong)') }}
      onMouseLeave={(e) => { compact ? setHover(false) : (e.currentTarget.style.borderColor = 'var(--border)') }}
    >
      {dot && (
        <span style={{
          width: 8, height: 8, borderRadius: '50%',
          background: dot, flexShrink: 0,
        }}/>
      )}
      <span style={{
        flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>{label}</span>
      <Icon name="chevD" size={13} sw={2} style={{
        color: 'var(--text-dim)', flexShrink: 0,
        opacity: compact ? (hover ? 1 : 0) : 1,
        transition: 'opacity .12s ease',
      }}/>
    </button>
  )
}

interface PickerStaticProps {
  dot?: string
  label?: string
  placeholder?: boolean
  compact?: boolean
}

function PickerStatic({ dot, label, placeholder, compact = false }: PickerStaticProps) {
  return (
    <div style={{
      width: '100%',
      display: 'flex', alignItems: 'center', gap: compact ? 7 : 8,
      padding: compact ? '0 6px' : '8px 0', minHeight: compact ? 30 : 32,
      fontFamily: 'var(--font-sans)', fontSize: compact ? 13 : 13.5, fontWeight: 500,
      color: placeholder ? 'var(--text-dim)' : 'var(--text-primary)',
    }}>
      {dot && (
        <span style={{
          width: 8, height: 8, borderRadius: '50%',
          background: dot, flexShrink: 0,
        }}/>
      )}
      <span style={{
        flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>{placeholder ? '—' : label}</span>
    </div>
  )
}

interface OptionListProps {
  children: React.ReactNode
  maxHeight?: number
  search?: React.ReactNode
  footer?: React.ReactNode
}

function OptionList({ children, maxHeight = 280, search, footer }: OptionListProps) {
  return (
    <div style={{ width: 260 }}>
      {search}
      <div style={{ maxHeight, overflowY: 'auto', padding: 4 }}>{children}</div>
      {footer}
    </div>
  )
}

interface OptionRowProps {
  dot?: string | null
  leading?: React.ReactNode
  label: string
  hint?: string | null
  checked?: boolean
  onClick?: (e?: React.MouseEvent) => void
  danger?: boolean
}

function OptionRow({ dot, leading, label, hint, checked = false, onClick, danger = false }: OptionRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%', textAlign: 'left', cursor: 'pointer',
        background: 'transparent', border: 'none',
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 10px', borderRadius: 'var(--r-sm)',
        fontFamily: 'var(--font-sans)', fontSize: 13,
        color: danger ? 'var(--st-error)' : 'var(--text-primary)',
        transition: 'background var(--dur-fast) var(--ease-out)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--row-hover)' }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
    >
      {dot && (
        <span style={{
          width: 8, height: 8, borderRadius: '50%',
          background: dot, flexShrink: 0,
        }}/>
      )}
      {leading}
      <span style={{
        flex: 1, fontWeight: 500,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{label}</span>
      {hint && (
        <span style={{
          fontFamily: 'var(--font-sans)', fontSize: 11.5, fontWeight: 500,
          color: 'var(--text-muted)',
        }}>{hint}</span>
      )}
      {checked && (
        <Icon name="check" size={13} sw={2} style={{ color: 'var(--brand-gold)', flexShrink: 0 }}/>
      )}
    </button>
  )
}

interface PopoverSearchProps {
  value: string
  onChange: (v: string) => void
  placeholder?: string
}

function PopoverSearch({ value, onChange, placeholder = 'Search…' }: PopoverSearchProps) {
  return (
    <div style={{ padding: 8, borderBottom: '1px solid var(--border)' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '6px 10px',
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-sm)',
      }}>
        <Icon name="search" size={12} sw={2} style={{ color: 'var(--text-muted)', flexShrink: 0 }}/>
        <input
          autoFocus
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{
            flex: 1, background: 'transparent', border: 'none', outline: 'none',
            fontFamily: 'var(--font-sans)', fontSize: 12.5,
            color: 'var(--text-primary)',
          }}
        />
      </div>
    </div>
  )
}

interface PopoverFooterProps {
  children: React.ReactNode
}

function PopoverFooter({ children }: PopoverFooterProps) {
  return (
    <div style={{
      padding: 8, borderTop: '1px solid var(--border)',
      display: 'flex', alignItems: 'center', gap: 8,
    }}>{children}</div>
  )
}

interface FooterActionProps {
  icon?: string
  label: string
  onClick?: () => void
  tone?: 'brand' | 'muted'
}

function FooterAction({ icon = 'plus', label, onClick, tone = 'brand' }: FooterActionProps) {
  const color = tone === 'brand' ? 'var(--brand-gold)' : 'var(--text-secondary)'
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: 'transparent', border: 'none', cursor: 'pointer',
        color, fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600,
        padding: '4px 6px', borderRadius: 'var(--r-sm)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--row-hover)' }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
    >
      <Icon name={icon} size={12} sw={2}/>
      {label}
    </button>
  )
}

// Simple 2-letter avatar chip (matches primitives Avatar visual)
interface AvatarChipProps {
  id?: string
  size?: number
}

function AvatarChip({ id = '?', size = 22 }: AvatarChipProps) {
  const initials = id.slice(0, 2).toUpperCase()
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: size, height: size, borderRadius: '50%',
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border)',
      fontFamily: 'var(--font-mono)', fontSize: size * 0.45, fontWeight: 700,
      color: 'var(--text-muted)', flexShrink: 0,
    }}>{initials}</span>
  )
}

// ─── SwatchStack ──────────────────────────────────────────────────────

export interface SwatchStackProps {
  swatches?: string[]
  size?: number
}

export function SwatchStack({ swatches = [], size = 14 }: SwatchStackProps) {
  if (!swatches.length) return null
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      {swatches.slice(0, 4).map((c, i) => (
        <span key={i} style={{
          display: 'inline-block',
          width: size, height: size, borderRadius: 4,
          background: c,
          border: '1px solid var(--border)',
          marginLeft: i === 0 ? 0 : -3,
          boxShadow: '0 0 0 1.5px var(--bg-surface)',
        }}/>
      ))}
    </span>
  )
}

// ─── MilestoneStatusPicker ────────────────────────────────────────────

export interface MilestoneStatusPickerProps {
  value?: string
  onChange?: (v: string) => void
  canEdit: boolean
}

export function MilestoneStatusPicker({ value = 'pending', onChange, canEdit }: MilestoneStatusPickerProps) {
  const cur = MILESTONE_STATUS.find(s => s.id === value) || MILESTONE_STATUS[0]
  if (!canEdit) return <PickerStatic dot={cur.dot} label={cur.label}/>
  return (
    <Popover
      side="bottom" align="start" width={220}
      trigger={<PickerTrigger dot={cur.dot} label={cur.label}/>}
      closeOnSelect
    >
      <OptionList>
        {MILESTONE_STATUS.map(s => (
          <OptionRow key={s.id} dot={s.dot} label={s.label}
            checked={s.id === value}
            onClick={() => onChange?.(s.id)}/>
        ))}
      </OptionList>
    </Popover>
  )
}

// ─── DeciderPicker ────────────────────────────────────────────────────

export interface DeciderPickerProps {
  value?: string | null
  currentName?: string | null
  onChange?: (v: string | null) => void
  canEdit: boolean
  options?: TeamOption[]
  compact?: boolean
}

export function DeciderPicker({ value = null, currentName, onChange, canEdit, options = [], compact = false }: DeciderPickerProps) {
  const [query, setQuery] = React.useState('')
  const clientOption: TeamOption = { id: 'CL', name: 'Sarah Holt (client)', role: 'Owner', avatar: 'SH' }
  const list = [clientOption, ...options]
  const cur = list.find(t => t.id === value)
  const displayName = cur?.name ?? currentName

  if (!canEdit) {
    if (!displayName) return <PickerStatic placeholder label="No decider" compact={compact}/>
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: compact ? '0 6px' : '6px 0',
        fontFamily: 'var(--font-sans)', fontSize: 13.5, fontWeight: 500,
      }}>
        {cur && <AvatarChip id={cur.avatar || cur.id} size={20}/>}
        {displayName}
        {cur?.role && (
          <span style={{ color: 'var(--text-muted)', fontSize: 11.5 }}>
            · {cur.role}
          </span>
        )}
      </div>
    )
  }

  return (
    <Popover
      side="bottom" align="start" width={280}
      trigger={
        <button
          type="button"
          style={compact ? {
            width: '100%', textAlign: 'left',
            display: 'flex', alignItems: 'center', gap: 7,
            padding: '0 6px', minHeight: 30,
            background: 'transparent', border: 'none',
            borderRadius: 6, cursor: 'pointer',
            fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500,
            color: displayName ? 'var(--text-primary)' : 'var(--text-dim)',
          } : {
            width: '100%', textAlign: 'left',
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 12px', minHeight: 40,
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-sm)', cursor: 'pointer',
            fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600,
            color: displayName ? 'var(--text-primary)' : 'var(--text-dim)',
          }}
        >
          {displayName ? (
            <>
              {cur && <AvatarChip id={cur.avatar || cur.id} size={20}/>}
              {displayName}
              <span style={{ flex: 1 }}/>
              <Icon name="chevD" size={14} sw={2} style={{ color: 'var(--text-muted)' }}/>
            </>
          ) : (
            <>
              <span style={{ flex: 1 }}>+ Pick decider…</span>
              <Icon name="chevD" size={14} sw={2} style={{ color: 'var(--text-muted)' }}/>
            </>
          )}
        </button>
      }
      closeOnSelect
    >
      <OptionList
        search={<PopoverSearch value={query} onChange={setQuery} placeholder="Search…"/>}
        footer={cur ? (
          <PopoverFooter>
            <FooterAction icon="x" label="Clear" tone="muted" onClick={() => onChange?.(null)}/>
          </PopoverFooter>
        ) : null}
      >
        {list
          .filter(p => !query || p.name.toLowerCase().includes(query.toLowerCase()))
          .map(p => (
            <OptionRow key={p.id}
              leading={<AvatarChip id={p.avatar || p.id} size={22}/>}
              label={p.name} hint={p.role}
              checked={p.id === value}
              onClick={() => onChange?.(p.id)}/>
          ))}
      </OptionList>
    </Popover>
  )
}

// ─── LocationPicker (hierarchical) ────────────────────────────────────

export function locPath(id: string, tree: LocationOption[]): LocationOption[] {
  const path: LocationOption[] = []
  let cur: LocationOption | undefined = tree.find(l => l.id === id)
  while (cur) {
    path.unshift(cur)
    const parentId = cur.parentId
    cur = parentId ? tree.find(l => l.id === parentId) : undefined
  }
  return path
}

export interface LocationPickerProps {
  value?: string | null
  currentName?: string | null
  onChange?: (v: string | null) => void
  canEdit: boolean
  locations?: LocationOption[]
  projectId?: string | null
  onCreateLocation?: (parentId?: string) => void
  compact?: boolean
}

export function LocationPicker({
  value = null,
  currentName,
  onChange,
  canEdit,
  locations = [],
  projectId = null,
  onCreateLocation,
  compact = false,
}: LocationPickerProps) {
  const [extraLocations, setExtraLocations] = React.useState<LocationOption[]>([])
  const allLocations = [...locations, ...extraLocations]
  const [query, setQuery] = React.useState('')
  const [inlineCreate, setInlineCreate] = React.useState<false | { parentId?: string }>(false)
  const [createName, setCreateName] = React.useState('')
  const [creating, setCreating] = React.useState(false)
  // Scope to the task's project ONLY when the location rows actually carry a
  // projectId. Many callers (TasksPage, kiosk) pass bare { id, name } location
  // rows with no projectId — in that case a strict equality filter empties the
  // whole list and the dropdown reads as inert. Keep any location lacking a
  // projectId so those callers still get a populated, selectable list.
  const scoped = projectId
    ? allLocations.filter(l => !l.projectId || l.projectId === projectId)
    : allLocations
  const cur = scoped.find(l => l.id === value) || null
  const projectRoot = scoped.find(l => l.isProject) || null

  const handleInlineCreateLoc = async () => {
    const name = createName.trim()
    if (!name || creating) return
    setCreating(true)
    try {
      const parentNumericId = (inlineCreate && inlineCreate.parentId) ? Number(inlineCreate.parentId) : undefined
      const body: Record<string, unknown> = { name, type: 'other' }
      if (parentNumericId) body.parent_id = parentNumericId
      const res = await authedFetch(`/api/v1/locations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }, { redirectOnAuthFailure: false })
      if (res.ok) {
        const created = await res.json()
        const newLoc: LocationOption = {
          id: String(created.id),
          name: created.name || name,
          parentId: (inlineCreate && inlineCreate.parentId) || null,
          projectId: projectId || undefined,
        }
        setExtraLocations(prev => [...prev, newLoc])
        onChange?.(newLoc.id)
      }
    } catch {/* silent */}
    setCreating(false)
    setInlineCreate(false)
    setCreateName('')
  }

  if (!canEdit) {
    if (!cur && !currentName) return <PickerStatic placeholder label="No location" compact={compact}/>
    if (!cur && currentName) {
      return (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: compact ? '0 6px' : '8px 0', minHeight: compact ? 30 : 32,
          fontFamily: 'var(--font-sans)', fontSize: 13.5, fontWeight: 500,
          color: 'var(--text-primary)',
        }}>
          {!compact && <Icon name="grid" size={13} sw={1.8} style={{ color: 'var(--text-muted)', flexShrink: 0 }}/>}
          <span>{currentName}</span>
        </div>
      )
    }
    const path = locPath(cur!.id, allLocations)
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: compact ? '0 6px' : '8px 0', minHeight: compact ? 30 : 32,
        fontFamily: 'var(--font-sans)', fontSize: 13.5, fontWeight: 500,
        color: 'var(--text-primary)',
      }}>
        {!compact && <Icon name="grid" size={13} sw={1.8} style={{ color: 'var(--text-muted)', flexShrink: 0 }}/>}
        {path.map((p, i) => (
          <React.Fragment key={p.id}>
            {i > 0 && (
              <Icon name="chevR" size={9} sw={2} style={{ color: 'var(--text-dim)' }}/>
            )}
            <span style={{
              color: i === path.length - 1 ? 'var(--text-primary)' : 'var(--text-muted)',
              fontWeight: i === path.length - 1 ? 600 : 500,
            }}>{p.name}</span>
          </React.Fragment>
        ))}
      </div>
    )
  }

  // Flat, depth-aware DFS list
  const flat: (LocationOption & { _depth: number })[] = []
  const visit = (parentId: string | null | undefined, depth: number) => {
    for (const loc of scoped.filter(l => l.parentId === (parentId ?? null) || (!l.parentId && !parentId))) {
      flat.push({ ...loc, _depth: depth })
      visit(loc.id, depth + 1)
    }
  }
  // seed with roots
  for (const loc of scoped.filter(l => !l.parentId)) {
    flat.push({ ...loc, _depth: 0 })
    visit(loc.id, 1)
  }

  const q = query.trim().toLowerCase()
  const filtered = q ? flat.filter(l => l.name.toLowerCase().includes(q)) : flat

  const triggerLabel = cur
    ? cur.name
    : currentName
      ? currentName
      : projectRoot
        ? `${projectRoot.name} · pick`
        : 'No location'

  const newLocationLabel = cur
    ? `New sub-location under ${cur.name}`
    : projectRoot
      ? `New sub-location under ${projectRoot.name}`
      : 'New location'

  const inlineCreateLocFooter = inlineCreate !== false ? (
    <div style={{ padding: 8, borderTop: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input
          autoFocus
          value={createName}
          onChange={e => setCreateName(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); handleInlineCreateLoc() }
            if (e.key === 'Escape') { setInlineCreate(false); setCreateName('') }
          }}
          placeholder="Location name…"
          style={{
            flex: 1, padding: '6px 8px',
            fontFamily: 'var(--font-sans)', fontSize: 12.5,
            color: 'var(--text-primary)',
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border-strong)',
            borderRadius: 'var(--r-sm)',
            outline: 'none',
          }}
        />
        <button type="button" onClick={handleInlineCreateLoc} disabled={creating || !createName.trim()} style={{
          padding: '5px 10px', borderRadius: 'var(--r-sm)',
          background: 'var(--brand-gold)', border: 'none',
          fontFamily: 'var(--font-sans)', fontSize: 12, fontWeight: 600,
          color: '#000', cursor: 'pointer', opacity: !createName.trim() ? 0.5 : 1,
        }}>
          {creating ? '…' : 'Create'}
        </button>
        <button type="button" onClick={() => { setInlineCreate(false); setCreateName('') }} style={{
          padding: '5px 8px', borderRadius: 'var(--r-sm)',
          background: 'transparent', border: '1px solid var(--border)',
          fontFamily: 'var(--font-sans)', fontSize: 12, cursor: 'pointer',
          color: 'var(--text-muted)',
        }}>✕</button>
      </div>
    </div>
  ) : null

  return (
    <Popover
      side="bottom" align="start" width={320}
      trigger={<PickerTrigger label={triggerLabel} placeholder={!cur && !currentName} compact={compact}/>}
      closeOnSelect
    >
      <OptionList
        search={<PopoverSearch value={query} onChange={setQuery} placeholder="Search locations…"/>}
        footer={
          inlineCreate !== false ? inlineCreateLocFooter : (
            <PopoverFooter>
              <FooterAction
                icon="plus" label={newLocationLabel}
                onClick={() => {
                  if (onCreateLocation) {
                    onCreateLocation?.(cur?.id)
                  } else {
                    setInlineCreate({ parentId: cur?.id })
                    setCreateName('')
                  }
                }}
              />
              <div style={{ flex: 1 }}/>
              {cur && (
                <FooterAction icon="x" label="Clear" tone="muted" onClick={() => onChange?.(null)}/>
              )}
            </PopoverFooter>
          )
        }
      >
        {filtered.length === 0 && (
          <div style={{
            padding: '14px 10px', fontFamily: 'var(--font-sans)',
            fontSize: 12.5, color: 'var(--text-muted)',
            fontStyle: 'italic', textAlign: 'center',
          }}>No locations match.</div>
        )}
        {filtered.map(l => {
          const isProj = !!l.isProject
          return (
            <button
              key={l.id}
              type="button"
              onClick={() => onChange?.(l.id)}
              style={{
                width: '100%', textAlign: 'left', cursor: 'pointer',
                background: 'transparent', border: 'none',
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 10px',
                paddingLeft: 10 + (q ? 0 : l._depth * 14),
                borderRadius: 'var(--r-sm)',
                fontFamily: 'var(--font-sans)', fontSize: 13,
                color: 'var(--text-primary)',
                transition: 'background var(--dur-fast) var(--ease-out)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--row-hover)' }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
            >
              <Icon
                name={isProj ? 'briefcase' : 'grid'} size={12} sw={1.8}
                style={{ color: isProj ? 'var(--brand-gold)' : 'var(--text-muted)', flexShrink: 0 }}
              />
              <span style={{
                flex: 1, fontWeight: isProj ? 700 : 500,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{l.name}</span>
              {l.hint && (
                <span style={{
                  fontFamily: 'var(--font-sans)', fontSize: 11,
                  color: 'var(--text-muted)', fontWeight: 500,
                }}>{l.hint}</span>
              )}
              {l.id === value && (
                <Icon name="check" size={12} sw={2} style={{ color: 'var(--brand-gold)', flexShrink: 0 }}/>
              )}
            </button>
          )
        })}
      </OptionList>
    </Popover>
  )
}

/** Legacy alias */
export const RoomPicker = LocationPicker

// ─── FSRoomPicker ─────────────────────────────────────────────────────────────
// Flat list picker sourced from decision_folders (the canonical FS room list).

export interface FSRoomOption {
  folder_id: string
  name: string
  location_id?: number | null
  /** Finish Schedule load on this room — feeds RoomSpecPill (SCHED-W4 §11). */
  finish_count?: number
  open_decision_count?: number
}

// ─── RoomSpecPill ─────────────────────────────────────────────────────
// Room annotation — counts finish selections on the room, goes amber with
// "n open" when decisions are outstanding. Information only, never a click
// target of its own (clicking the row selects the room — SCHED-W4 §11).
export function RoomSpecPill({ room }: { room: Pick<FSRoomOption, 'finish_count' | 'open_decision_count'> }) {
  const finishCount = room.finish_count ?? 0
  const openCount = room.open_decision_count ?? 0
  if (!finishCount && !openCount) return null
  const warn = openCount > 0
  return (
    <span
      title={`${finishCount} finish selection${finishCount === 1 ? '' : 's'}${openCount ? ` · ${openCount} decision${openCount > 1 ? 's' : ''} open` : ''}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0,
        padding: '1px 6px', borderRadius: 999,
        background: warn ? 'color-mix(in srgb, var(--st-warning) 14%, transparent)' : 'var(--brand-gold-soft)',
        border: `1px solid ${warn ? 'color-mix(in srgb, var(--st-warning) 35%, transparent)' : 'var(--brand-gold-border)'}`,
        color: warn ? 'var(--st-warning)' : 'var(--brand-gold)',
        fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700,
        letterSpacing: 0.2, whiteSpace: 'nowrap',
      }}
    >
      <Icon name="swatches" size={9} sw={2.2}/>
      {finishCount}
      {openCount > 0 && <span style={{ opacity: 0.85 }}>· {openCount} open</span>}
    </span>
  )
}

export interface FSRoomPickerProps {
  value?: string | null
  currentName?: string | null
  onChange?: (v: string | null) => void
  canEdit: boolean
  options?: FSRoomOption[]
  /**
   * When set, typing runs a debounced (300ms) real backend search (`?q=`)
   * instead of client-filtering `options`. The unsearched view is left to
   * the caller's `options` list unchanged (some callers, e.g. TaskForm's
   * inline room picker, pre-filter `options` by the task's location — a
   * scoping concern independent of text search, so it isn't replaced by a
   * live fetch). Matches the AssigneePicker `projectId` search contract
   * (#803 AC3) without discarding that caller-side scoping.
   */
  projectId?: string | number | null
  compact?: boolean
}

export function FSRoomPicker({
  value = null,
  currentName,
  onChange,
  canEdit,
  options = [],
  projectId,
  compact = false,
}: FSRoomPickerProps) {
  const [query, setQuery] = React.useState('')

  const [liveRooms, setLiveRooms] = React.useState<FSRoomOption[] | null>(null)
  React.useEffect(() => {
    const q = query.trim()
    if (!projectId || !q) { setLiveRooms(null); return }
    let cancelled = false
    const handle = window.setTimeout(async () => {
      try {
        const res = await fsRoomsApi.listForProject(projectId, q)
        if (!cancelled) setLiveRooms(res.rooms)
      } catch {
        if (!cancelled) setLiveRooms([])
      }
    }, 300)
    return () => { cancelled = true; window.clearTimeout(handle) }
  }, [projectId, query])

  const effectiveOptions = liveRooms ?? options
  const cur = options.find(r => r.folder_id === value) || null
  const displayName = cur?.name ?? currentName

  if (!canEdit) {
    if (!displayName) return <PickerStatic placeholder label="No room" compact={compact}/>
    return (
      <div style={{
        padding: compact ? '0 6px' : '6px 0',
        minHeight: compact ? 30 : undefined,
        display: compact ? 'flex' : undefined, alignItems: compact ? 'center' : undefined,
        fontFamily: 'var(--font-sans)', fontSize: 13.5, fontWeight: 500,
        color: 'var(--text-primary)',
      }}>
        {displayName}
      </div>
    )
  }

  return (
    <Popover
      side="bottom" align="start" width={260}
      trigger={
        <button
          type="button"
          style={compact ? {
            width: '100%', textAlign: 'left',
            display: 'flex', alignItems: 'center', gap: 7,
            padding: '0 6px', minHeight: 30,
            background: 'transparent', border: 'none',
            borderRadius: 6, cursor: 'pointer',
            fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500,
            color: displayName ? 'var(--text-primary)' : 'var(--text-dim)',
          } : {
            width: '100%', textAlign: 'left',
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 12px', minHeight: 40,
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-sm)', cursor: 'pointer',
            fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600,
            color: displayName ? 'var(--text-primary)' : 'var(--text-dim)',
          }}
        >
          <span style={{ flex: 1 }}>{displayName ?? '+ Pick room…'}</span>
          <Icon name="chevD" size={13} sw={2} style={{ color: 'var(--text-dim)' }}/>
        </button>
      }
      closeOnSelect
    >
      <OptionList
        search={<PopoverSearch value={query} onChange={setQuery} placeholder="Search rooms…"/>}
        footer={cur ? (
          <PopoverFooter>
            <FooterAction icon="x" label="Clear room" tone="muted" onClick={() => onChange?.(null)}/>
          </PopoverFooter>
        ) : null}
      >
        {effectiveOptions.length === 0 ? (
          <div style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 13 }}>
            {liveRooms ? 'No rooms match.' : 'No rooms — add rooms in Finish Schedule first'}
          </div>
        ) : (liveRooms
            // Live mode: server already applied the `q` search, no client filter.
            ? effectiveOptions
            : effectiveOptions.filter(r => !query || r.name.toLowerCase().includes(query.toLowerCase()))
          ).map(r => (
              <OptionRow key={r.folder_id}
                label={r.name}
                checked={r.folder_id === value}
                onClick={() => onChange?.(r.folder_id)}/>
            ))}
      </OptionList>
    </Popover>
  )
}

// ─── LocationRoomPicker ────────────────────────────────────────────────
// Consolidates the Location (locations table) and FS Room (decision_folders,
// nested under a location via location_id) into ONE hierarchical picker.
// #1441: showing them as two separate rows reads as redundant — Room and its
// parent Location frequently share the same name (e.g. a "Bathroom" room
// inside a "Bathroom" location), so the peek showed the same value twice.
// Picking a Room sets both roomId and its parent locationId; picking a bare
// Location clears the room. Only one row, one value, ever.

export interface LocationRoomPickerProps {
  locationValue?: string | null
  roomValue?: string | null
  currentLocationName?: string | null
  currentRoomName?: string | null
  onChange?: (v: { locationId: string | null; roomId: string | null; roomName?: string | null }) => void
  canEdit: boolean
  locations?: LocationOption[]
  rooms?: FSRoomOption[]
  projectId?: string | null
  compact?: boolean
  /** Override the closed-state trigger element (default: PickerTrigger's flat
   *  single-line label). Used by callers that need a richer closed display —
   *  e.g. a full breadcrumb with the parent muted and the room emphasised —
   *  without duplicating the popover/selection logic below. */
  renderTrigger?: (label: string, placeholder: boolean) => React.ReactNode
}

export function LocationRoomPicker({
  locationValue = null,
  roomValue = null,
  currentLocationName,
  currentRoomName,
  onChange,
  canEdit,
  locations = [],
  rooms = [],
  projectId = null,
  compact = false,
  renderTrigger,
}: LocationRoomPickerProps) {
  const [query, setQuery] = React.useState('')

  const scoped = projectId
    ? locations.filter(l => !l.projectId || l.projectId === projectId)
    : locations

  const curRoom = rooms.find(r => r.folder_id === roomValue) || null
  const curLoc = scoped.find(l => l.id === locationValue) || null
  const hasValue = !!(curRoom || curLoc || roomValue || locationValue || currentRoomName || currentLocationName)

  if (!canEdit) {
    if (!hasValue) return <PickerStatic placeholder label="No location" compact={compact}/>
    const roomName = curRoom?.name ?? currentRoomName ?? null
    const roomLocId = curRoom?.location_id != null ? String(curRoom.location_id) : locationValue
    const path = roomLocId ? locPath(roomLocId, scoped).map(p => p.name) : (currentLocationName ? [currentLocationName] : [])
    const segments = [...path]
    if (roomName && segments[segments.length - 1] !== roomName) segments.push(roomName)
    const deduped = segments.filter((s, i) => i === 0 || s !== segments[i - 1])
    if (deduped.length === 0) return <PickerStatic placeholder label="No location" compact={compact}/>
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: compact ? '0 6px' : '8px 0', minHeight: compact ? 30 : 32,
        fontFamily: 'var(--font-sans)', fontSize: 13.5, fontWeight: 500,
        color: 'var(--text-primary)',
      }}>
        {!compact && <Icon name="grid" size={13} sw={1.8} style={{ color: 'var(--text-muted)', flexShrink: 0 }}/>}
        {deduped.map((seg, i) => (
          <React.Fragment key={i}>
            {i > 0 && <Icon name="chevR" size={9} sw={2} style={{ color: 'var(--text-dim)' }}/>}
            <span style={{
              color: i === deduped.length - 1 ? 'var(--text-primary)' : 'var(--text-muted)',
              fontWeight: i === deduped.length - 1 ? 600 : 500,
            }}>{seg}</span>
          </React.Fragment>
        ))}
        {curRoom && <RoomSpecPill room={curRoom}/>}
      </div>
    )
  }

  // Direct room count per location — feeds the "N ROOMS"/"NO ROOMS" annotation
  // next to each location row (SCHED-W4 §11: "the right-side count says
  // whether a location has rooms at all").
  const directRoomCount = new Map<string, number>()
  for (const rm of rooms) {
    if (rm.location_id == null) continue
    const key = String(rm.location_id)
    directRoomCount.set(key, (directRoomCount.get(key) ?? 0) + 1)
  }

  // Flat, depth-aware DFS list — locations in their tree order, with any FS
  // rooms belonging to a location nested directly underneath it.
  type Row =
    | { kind: 'location'; loc: LocationOption; depth: number }
    | { kind: 'room'; room: FSRoomOption; depth: number }

  const flat: Row[] = []
  const visit = (parentId: string | null, depth: number) => {
    for (const loc of scoped.filter(l => (l.parentId ?? null) === parentId)) {
      flat.push({ kind: 'location', loc, depth })
      for (const r of rooms.filter(r => r.location_id != null && String(r.location_id) === loc.id)) {
        flat.push({ kind: 'room', room: r, depth: depth + 1 })
      }
      visit(loc.id, depth + 1)
    }
  }
  visit(null, 0)

  // FS rooms whose parent Location was deleted (location_id nulled via
  // ON DELETE SET NULL) have nowhere to nest — surface them in their own
  // fallback group so they stay selectable instead of vanishing.
  const orphanRooms = rooms.filter(r => r.location_id == null)

  const q = query.trim().toLowerCase()
  const filtered = q
    ? flat.filter(row => (row.kind === 'location' ? row.loc.name : row.room.name).toLowerCase().includes(q))
    : flat
  const filteredOrphans = q
    ? orphanRooms.filter(r => r.name.toLowerCase().includes(q))
    : orphanRooms

  const triggerLabel = curRoom
    ? curRoom.name
    : curLoc
      ? curLoc.name
      : currentRoomName || currentLocationName || 'No location'

  return (
    <Popover
      side="bottom" align="start" width={320}
      trigger={renderTrigger ? renderTrigger(triggerLabel, !hasValue) : <PickerTrigger label={triggerLabel} placeholder={!hasValue} compact={compact}/>}
      closeOnSelect
    >
      <OptionList
        search={<PopoverSearch value={query} onChange={setQuery} placeholder="Search locations & rooms…"/>}
        footer={
          <PopoverFooter>
            {hasValue && (
              <FooterAction icon="x" label="Clear" tone="muted"
                onClick={() => onChange?.({ locationId: null, roomId: null, roomName: null })}/>
            )}
            <div style={{ flex: 1 }}/>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0,
              fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: 0.3,
              color: 'var(--text-dim)', textTransform: 'uppercase',
            }}>
              <Icon name="swatches" size={9} sw={2.2}/> = finish selections on room
            </span>
          </PopoverFooter>
        }
      >
        {filtered.length === 0 && filteredOrphans.length === 0 && (
          <div style={{
            padding: '14px 10px', fontFamily: 'var(--font-sans)',
            fontSize: 12.5, color: 'var(--text-muted)',
            fontStyle: 'italic', textAlign: 'center',
          }}>No locations match.</div>
        )}
        {filtered.map(row => {
          if (row.kind === 'location') {
            const isProj = !!row.loc.isProject
            const checked = !roomValue && row.loc.id === locationValue
            return (
              <button
                key={`loc-${row.loc.id}`}
                type="button"
                onClick={() => onChange?.({ locationId: row.loc.id, roomId: null, roomName: null })}
                style={{
                  width: '100%', textAlign: 'left', cursor: 'pointer',
                  background: 'transparent', border: 'none',
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '7px 10px',
                  paddingLeft: 10 + (q ? 0 : row.depth * 18),
                  borderRadius: 'var(--r-sm)',
                  fontFamily: 'var(--font-sans)', fontSize: 13,
                  color: 'var(--text-primary)',
                  transition: 'background var(--dur-fast) var(--ease-out)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--row-hover)' }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
              >
                <Icon
                  name={isProj ? 'home' : 'grid'} size={12} sw={1.8}
                  style={{ color: isProj ? 'var(--brand-gold)' : 'var(--text-muted)', flexShrink: 0 }}
                />
                <span style={{
                  flex: 1, fontWeight: isProj ? 700 : 500,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{row.loc.name}</span>
                <span style={{
                  fontFamily: 'var(--font-mono)', fontSize: 9.5, color: 'var(--text-dim)',
                  letterSpacing: 0.2, flexShrink: 0,
                }}>
                  {(directRoomCount.get(row.loc.id) ?? 0) > 0
                    ? `${directRoomCount.get(row.loc.id)} ROOM${(directRoomCount.get(row.loc.id) ?? 0) > 1 ? 'S' : ''}`
                    : 'NO ROOMS'}
                </span>
                {checked && (
                  <Icon name="check" size={12} sw={2} style={{ color: 'var(--brand-gold)', flexShrink: 0 }}/>
                )}
              </button>
            )
          }
          const checked = row.room.folder_id === roomValue
          return (
            <button
              key={`room-${row.room.folder_id}`}
              type="button"
              onClick={() => onChange?.({
                locationId: row.room.location_id != null ? String(row.room.location_id) : locationValue,
                roomId: row.room.folder_id,
                roomName: row.room.name,
              })}
              style={{
                width: '100%', textAlign: 'left', cursor: 'pointer',
                background: 'transparent', border: 'none',
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 10px',
                paddingLeft: 10 + (q ? 0 : row.depth * 18),
                borderRadius: 'var(--r-sm)',
                fontFamily: 'var(--font-sans)', fontSize: 13,
                color: 'var(--text-primary)',
                transition: 'background var(--dur-fast) var(--ease-out)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--row-hover)' }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
            >
              <Icon name="grid" size={12} sw={1.8} style={{ color: 'var(--text-muted)', flexShrink: 0 }}/>
              <span style={{
                flex: 1, fontWeight: 500,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{row.room.name}</span>
              <RoomSpecPill room={row.room}/>
              {checked && (
                <Icon name="check" size={12} sw={2} style={{ color: 'var(--brand-gold)', flexShrink: 0 }}/>
              )}
            </button>
          )
        })}
        {filteredOrphans.length > 0 && (
          <>
            <div style={{
              padding: '8px 10px 4px',
              fontFamily: 'var(--font-sans)', fontSize: 10.5, fontWeight: 700,
              letterSpacing: 0.6, textTransform: 'uppercase',
              color: 'var(--text-muted)',
            }}>Unassigned</div>
            {filteredOrphans.map(r => {
              const checked = r.folder_id === roomValue
              return (
                <button
                  key={`orphan-room-${r.folder_id}`}
                  type="button"
                  onClick={() => onChange?.({
                    locationId: locationValue,
                    roomId: r.folder_id,
                    roomName: r.name,
                  })}
                  style={{
                    width: '100%', textAlign: 'left', cursor: 'pointer',
                    background: 'transparent', border: 'none',
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '7px 10px', paddingLeft: 10,
                    borderRadius: 'var(--r-sm)',
                    fontFamily: 'var(--font-sans)', fontSize: 13,
                    color: 'var(--text-primary)',
                    transition: 'background var(--dur-fast) var(--ease-out)',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--row-hover)' }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
                >
                  <Icon name="grid" size={12} sw={1.8} style={{ color: 'var(--text-muted)', flexShrink: 0 }}/>
                  <span style={{
                    flex: 1, fontWeight: 500,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{r.name}</span>
                  {checked && (
                    <Icon name="check" size={12} sw={2} style={{ color: 'var(--brand-gold)', flexShrink: 0 }}/>
                  )}
                </button>
              )
            })}
          </>
        )}
      </OptionList>
    </Popover>
  )
}

// ─── LinkedSpecPicker ────────────────────────────────────────────────

interface SpecRowProps {
  s: SpecOption
  checked: boolean
  onClick?: (e?: React.MouseEvent) => void
}

function SpecRow({ s, checked, onClick }: SpecRowProps) {
  const tint = SPEC_TYPE_TINT[s.specType || ''] || 'var(--text-muted)'
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%', textAlign: 'left', cursor: 'pointer',
        background: 'transparent', border: 'none',
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 10px', borderRadius: 'var(--r-sm)',
        transition: 'background var(--dur-fast) var(--ease-out)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--row-hover)' }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
    >
      <span style={{
        width: 10, height: 10, borderRadius: 3, background: tint,
        flexShrink: 0,
      }}/>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600,
          color: 'var(--text-primary)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{s.name}</div>
        <div style={{
          fontFamily: 'var(--font-sans)', fontSize: 11, color: 'var(--text-muted)',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          {s.room && <span>{s.room}</span>}
          {s.room && s.specType && <span>·</span>}
          {s.specType && <span style={{ textTransform: 'capitalize' }}>{s.specType}</span>}
          {s.placeholder && (
            <><span>·</span><span style={{ fontStyle: 'italic' }}>placeholder</span></>
          )}
        </div>
      </div>
      {s.swatches && <SwatchStack swatches={s.swatches} size={12}/>}
      {checked && (
        <Icon name="check" size={13} sw={2} style={{ color: 'var(--brand-gold)', flexShrink: 0 }}/>
      )}
    </button>
  )
}

export interface LinkedSpecPickerProps {
  value?: SpecOption | null
  /** onChange receives the full SpecOption, or null to unlink */
  onChange?: (v: SpecOption | null) => void
  canEdit: boolean
  specOptions?: SpecOption[]
}

export function LinkedSpecPicker({ value = null, onChange, canEdit, specOptions = [] }: LinkedSpecPickerProps) {
  const [query, setQuery] = React.useState('')

  if (!canEdit) {
    if (!value) return <PickerStatic placeholder label="Not linked to a spec"/>
    return <SpecRow s={value} checked={false}/>
  }

  const filtered = specOptions.filter(s =>
    !query
      || s.name.toLowerCase().includes(query.toLowerCase())
      || (s.room || '').toLowerCase().includes(query.toLowerCase())
  )

  return (
    <Popover
      side="bottom" align="start" width={340}
      trigger={
        value ? (
          <div
            role="button"
            tabIndex={0}
            style={{
              width: '100%', textAlign: 'left',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-sm)', cursor: 'pointer',
              padding: 2,
            }}
          >
            <SpecRow s={value} checked={false}/>
          </div>
        ) : (
          <PickerTrigger label="+ Link to a Finish Schedule item…" placeholder/>
        )
      }
      closeOnSelect
    >
      <OptionList
        search={<PopoverSearch value={query} onChange={setQuery} placeholder="Search specs by name or room…"/>}
        footer={
          <PopoverFooter>
            <FooterAction icon="plus" label="New placeholder spec"/>
            <div style={{ flex: 1 }}/>
            {value && (
              <FooterAction icon="x" label="Unlink" tone="muted" onClick={() => onChange?.(null)}/>
            )}
          </PopoverFooter>
        }
      >
        {filtered.map(s => (
          <SpecRow key={s.id} s={s}
            checked={value?.id === s.id}
            onClick={() => onChange?.(s)}/>
        ))}
      </OptionList>
    </Popover>
  )
}

// ─── LinkedEstimateLineItemPicker ──────────────────────────────────────
// Schedule↔Estimate link (#2936) — lets a task/phase point at the estimate
// line item that justifies the work. Mirrors LinkedSpecPicker's
// popover/search/footer shape; "create new" hands the typed query back to
// the caller, which mirrors the estimate editor's add-line-item flow
// (create the row, then link it) rather than duplicating that logic here.

export interface EstimateLineItemOption {
  id: number
  description: string
  total?: number
  /** Set only when the task's project has more than one estimate, so rows
   *  can be grouped by which estimate they belong to. */
  estimateTitle?: string
  // ── Cost facet fields (UOW-1A #3586) — read-only summary of the live
  // estimate line, populated only for users who can view estimates.
  // markup_pct is estimate FRACTION scale (0–1): display as ×100 percent.
  quantity?: number
  unit?: string
  unit_cost?: number
  markup_pct?: number
  markup_type?: 'percent' | 'flat'
  markup_flat_amount?: number
  markup_amount?: number
  subtotal?: number
  /** Always set (unlike estimateTitle) — the Cost facet names the estimate. */
  estimateName?: string
}

export interface LinkedEstimateLineItemPickerProps {
  value?: EstimateLineItemOption | null
  /** onChange receives the full option, or null to unlink */
  onChange?: (v: EstimateLineItemOption | null) => void
  /** Called with the raw search text when the user picks "Create <query>" —
   *  the caller creates the line item and then calls onChange with it. */
  onCreateNew?: (description: string) => void
  canEdit: boolean
  options?: EstimateLineItemOption[]
}

function EstimateLineItemRow({ item, checked, onClick }: { item: EstimateLineItemOption; checked: boolean; onClick?: () => void }) {
  const row = (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '8px 10px', borderRadius: 'var(--r-sm)',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600,
          color: 'var(--text-primary)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{item.description || '(untitled line item)'}</div>
        {(item.estimateTitle || item.total != null) && (
          <div style={{
            fontFamily: 'var(--font-sans)', fontSize: 11, color: 'var(--text-muted)',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            {item.estimateTitle && <span>{item.estimateTitle}</span>}
            {item.estimateTitle && item.total != null && <span>·</span>}
            {item.total != null && <span>{formatCurrency(item.total)}</span>}
          </div>
        )}
      </div>
      {checked && (
        <Icon name="check" size={13} sw={2} style={{ color: 'var(--brand-gold)', flexShrink: 0 }}/>
      )}
    </div>
  )
  if (!onClick) return row
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%', textAlign: 'left', cursor: 'pointer',
        background: 'transparent', border: 'none', padding: 0,
        transition: 'background var(--dur-fast) var(--ease-out)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--row-hover)' }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
    >
      {row}
    </button>
  )
}

export function LinkedEstimateLineItemPicker({ value = null, onChange, onCreateNew, canEdit, options = [] }: LinkedEstimateLineItemPickerProps) {
  const [query, setQuery] = React.useState('')

  if (!canEdit) {
    if (!value) return <PickerStatic placeholder label="Not linked to an estimate line item"/>
    return <EstimateLineItemRow item={value} checked={false}/>
  }

  const q = query.toLowerCase().trim()
  const filtered = options.filter(o => !q || o.description.toLowerCase().includes(q))
  const exactMatch = q && filtered.some(o => o.description.toLowerCase() === q)

  return (
    <Popover
      side="bottom" align="start" width={340}
      trigger={
        value ? (
          <div
            role="button"
            tabIndex={0}
            style={{
              width: '100%', textAlign: 'left',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-sm)', cursor: 'pointer',
              padding: 2,
            }}
          >
            <EstimateLineItemRow item={value} checked={false}/>
          </div>
        ) : (
          <PickerTrigger label="+ Link to an estimate line item…" placeholder/>
        )
      }
      closeOnSelect
    >
      <OptionList
        search={<PopoverSearch value={query} onChange={setQuery} placeholder="Search estimate line items…"/>}
        footer={
          <PopoverFooter>
            {q && !exactMatch && (
              <FooterAction icon="plus" label={`Create "${query}"`} onClick={() => onCreateNew?.(query)}/>
            )}
            <div style={{ flex: 1 }}/>
            {value && (
              <FooterAction icon="x" label="Unlink" tone="muted" onClick={() => onChange?.(null)}/>
            )}
          </PopoverFooter>
        }
      >
        {filtered.map(o => (
          <EstimateLineItemRow key={o.id} item={o}
            checked={value?.id === o.id}
            onClick={() => onChange?.(o)}/>
        ))}
      </OptionList>
    </Popover>
  )
}

// ─── DepTypeBadge ─────────────────────────────────────────────────────

export interface DepTypeBadgeProps {
  value?: string
  onChange?: (t: string) => void
  canEdit: boolean
}

export function DepTypeBadge({ value = 'FS', onChange, canEdit }: DepTypeBadgeProps) {
  const cur = DEP_TYPES.find(t => t.id === value) || DEP_TYPES[0]
  const pill = (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center',
        padding: '2px 6px', borderRadius: 4,
        background: 'color-mix(in srgb, var(--text-muted) 18%, transparent)',
        color: 'var(--text-secondary)',
        fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700,
        letterSpacing: 0.6,
      }}
      title={cur.tip}
    >{cur.label}</span>
  )
  if (!canEdit) return pill
  return (
    <Popover
      side="bottom" align="end" width={220}
      trigger={
        <button
          type="button"
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
          title={cur.tip}
        >{pill}</button>
      }
      closeOnSelect
    >
      <OptionList>
        {DEP_TYPES.map(t => (
          <OptionRow key={t.id}
            label={`${t.label} · ${t.hint}`}
            checked={t.id === value}
            onClick={() => onChange?.(t.id)}/>
        ))}
        <div style={{
          padding: '6px 10px', marginTop: 4,
          borderTop: '1px solid var(--border)',
          fontFamily: 'var(--font-sans)', fontSize: 11,
          color: 'var(--text-muted)', lineHeight: 1.4,
        }}>
          {DEP_TYPES.find(t => t.id === value)?.tip}
        </div>
      </OptionList>
    </Popover>
  )
}

// ─── LeadLagInput ─────────────────────────────────────────────────────

export interface LeadLagInputProps {
  value?: number
  onChange?: (n: number) => void
  canEdit: boolean
}

export function LeadLagInput({ value = 0, onChange, canEdit }: LeadLagInputProps) {
  const fmt = (n: number) => n > 0 ? `+${n}d` : `${n}d`
  if (!canEdit) {
    if (!value) return null
    return (
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700,
        color: value < 0 ? 'var(--st-warning)' : 'var(--text-muted)',
      }}>{fmt(value)}</span>
    )
  }
  return (
    <input
      defaultValue={value || ''}
      onBlur={(e) => {
        const t = e.target.value.trim().replace(/[d ]/g, '')
        const n = t === '' ? 0 : parseInt(t, 10)
        onChange?.(Number.isFinite(n) ? n : 0)
      }}
      placeholder="0d"
      title="Lead (negative) or lag (positive), in working days"
      style={{
        width: 42, padding: '2px 4px',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 4, outline: 'none',
        fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600,
        color: 'var(--text-primary)',
        textAlign: 'center',
      }}
    />
  )
}

// ─── DependencyRow ────────────────────────────────────────────────────

export interface DependencyRowProps {
  item: DepItem
  depType?: string
  lag?: number
  onRemove?: () => void
  onChangeType?: (t: string) => void
  onChangeLag?: (n: number) => void
  onOpenSpec?: (id: string) => void
  /** TASK-DETAIL-PEEK-DEP-HOVER-PREVIEW — opens stacked peek for task deps */
  onOpenTask?: (id: string) => void
  canEdit: boolean
}

export function DependencyRow({
  item,
  depType,
  lag = 0,
  onRemove,
  onChangeType,
  onChangeLag,
  onOpenSpec,
  onOpenTask,
  canEdit,
}: DependencyRowProps) {
  const isSpec = item.type === 'spec'
  const isPermitMilestone = item.type === 'permit_milestone'
  const isPhase = item.type === 'phase'
  const specApproved = isSpec && item.status === 'approved'

  // ── Hover preview ────────────────────────────────────────────────────
  const rowRef = React.useRef<HTMLDivElement>(null)
  const [hoverOpen, setHoverOpen] = React.useState(false)
  const [hoverPos, setHoverPos] = React.useState({ top: 0, left: 0, width: 0 })
  const showTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const hideTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  React.useEffect(() => () => {
    if (showTimerRef.current) clearTimeout(showTimerRef.current)
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
  }, [])

  const scheduleShow = () => {
    if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null }
    if (!showTimerRef.current) {
      showTimerRef.current = setTimeout(() => {
        showTimerRef.current = null
        const rect = rowRef.current?.getBoundingClientRect()
        if (rect) setHoverPos({ top: rect.bottom + 4, left: rect.left, width: rect.width })
        setHoverOpen(true)
      }, 200)
    }
  }
  const scheduleHide = () => {
    if (showTimerRef.current) { clearTimeout(showTimerRef.current); showTimerRef.current = null }
    if (!hideTimerRef.current) {
      hideTimerRef.current = setTimeout(() => {
        hideTimerRef.current = null
        setHoverOpen(false)
      }, 100)
    }
  }

  // Status pill colors mirror task status tokens
  const statusDot = (s?: string) => {
    if (!s) return 'var(--text-muted)'
    const k = s.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_')
    return `var(--status-${k}-dot, var(--text-muted))`
  }
  const statusLabel = (s?: string) => {
    if (!s) return null
    return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  }
  const canOpen = (isSpec && !!onOpenSpec) || (!isSpec && !isPermitMilestone && !!onOpenTask)

  // Permit-milestone stage pill colors (mirrors PERMIT_STAGES in permits/index.tsx)
  const permitStageMeta: Record<string, { dot: string; bg: string; tx: string; label: string }> = {
    draft:            { dot: 'var(--status-not-started-dot)', bg: 'var(--status-not-started-bg)', tx: 'var(--status-not-started-tx)', label: 'Draft' },
    submitted:        { dot: 'var(--st-info)',    bg: 'var(--st-info-soft)',    tx: 'var(--st-info)',    label: 'Submitted' },
    in_review:        { dot: 'var(--st-warning)', bg: 'var(--st-warning-soft)', tx: 'var(--status-in-progress-tx)', label: 'In Review' },
    conditions:       { dot: 'var(--st-warning)', bg: 'var(--st-warning-soft)', tx: 'var(--status-in-progress-tx)', label: 'Conditions' },
    contested:        { dot: 'var(--st-error)',   bg: 'var(--st-error-soft)',   tx: 'var(--status-blocked-tx)',     label: 'Contested' },
    issued:           { dot: 'var(--st-success)', bg: 'var(--st-success-soft)', tx: 'var(--status-completed-tx)',  label: 'Issued' },
    under_inspection: { dot: 'var(--brand-gold)', bg: 'var(--brand-gold-soft)', tx: 'var(--brand-gold)',           label: 'Inspecting' },
    suspended:        { dot: 'var(--st-error)',   bg: 'var(--st-error-soft)',   tx: 'var(--status-blocked-tx)',     label: 'Suspended' },
    revoked:          { dot: 'var(--st-error)',   bg: 'var(--st-error-soft)',   tx: 'var(--status-blocked-tx)',     label: 'Revoked' },
    expired:          { dot: 'var(--text-muted)', bg: 'var(--bg-elevated)',     tx: 'var(--text-muted)',            label: 'Expired' },
    closed:           { dot: 'var(--st-info)',    bg: 'var(--st-info-soft)',    tx: 'var(--st-info)',               label: 'Closed' },
  }
  const stageMeta = permitStageMeta[item.permitStage || 'draft'] ?? permitStageMeta.draft

  // BUG-DEP-PICKER-DISAMBIG — phase/room shown as a secondary line on the
  // selected chip too, so same-named deps stay distinguishable post-selection.
  const depHint = (!isSpec && !isPermitMilestone) ? [item.phase, item.room].filter(Boolean).join(' · ') : ''

  return (
    <>
    <div ref={rowRef}
      onMouseEnter={scheduleShow}
      onMouseLeave={scheduleHide}
      style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '6px 8px',
      background: isPermitMilestone ? 'color-mix(in srgb, var(--st-info) 6%, var(--bg-elevated))' : 'var(--bg-elevated)',
      border: `1px solid ${isPermitMilestone ? 'color-mix(in srgb, var(--st-info) 22%, var(--border))' : 'var(--border)'}`,
      borderRadius: 'var(--r-sm)',
      fontFamily: 'var(--font-sans)', fontSize: 12.5,
    }}>
      {/* Type badge */}
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '2px 6px', borderRadius: 4,
        background: isSpec
          ? 'color-mix(in srgb, var(--brand-gold) 18%, transparent)'
          : isPermitMilestone
            ? 'color-mix(in srgb, var(--st-info) 18%, transparent)'
            : isPhase
              ? 'color-mix(in srgb, var(--status-in-progress-dot) 18%, transparent)'
              : 'color-mix(in srgb, var(--text-muted) 18%, transparent)',
        color: isSpec ? 'var(--brand-gold)' : isPermitMilestone ? 'var(--st-info)' : isPhase ? 'var(--status-in-progress-dot)' : 'var(--text-secondary)',
        fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700,
        letterSpacing: 0.6, textTransform: 'uppercase',
      }}>
        {/* Diamond ◆ for permit milestones */}
        {isPermitMilestone && (
          <span style={{ fontSize: 8, lineHeight: 1 }}>◆</span>
        )}
        {isSpec ? 'spec' : isPermitMilestone ? 'permit' : isPhase ? 'phase' : 'task'}
      </span>

      {/* Name */}
      {isSpec && onOpenSpec ? (
        <button
          type="button"
          onClick={() => onOpenSpec(item.id)}
          style={{
            flex: 1, display: 'flex', alignItems: 'center', gap: 6,
            background: 'transparent', border: 'none', cursor: 'pointer',
            padding: 0, textAlign: 'left',
            color: 'var(--text-primary)', fontWeight: 500,
            fontFamily: 'var(--font-sans)', fontSize: 12.5,
            minWidth: 0,
          }}
        >
          <span style={{
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{item.name}</span>
          <Icon name="chevR" size={11} sw={2} style={{ color: 'var(--text-muted)', flexShrink: 0 }}/>
        </button>
      ) : (onOpenTask && !isPermitMilestone) ? (
        <button
          type="button"
          onClick={() => onOpenTask(item.id)}
          style={{
            flex: 1, display: 'flex', alignItems: 'center', gap: 6,
            background: 'transparent', border: 'none', cursor: 'pointer',
            padding: 0, textAlign: 'left',
            color: 'var(--text-primary)', fontWeight: 500,
            fontFamily: 'var(--font-sans)', fontSize: 12.5,
            minWidth: 0,
          }}
        >
          <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
            {depHint && (
              <span style={{
                fontSize: 10, fontWeight: 400, color: 'var(--text-muted)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{depHint}</span>
            )}
          </span>
          <Icon name="chevR" size={11} sw={2} style={{ color: 'var(--text-muted)', flexShrink: 0 }}/>
        </button>
      ) : (
        <span style={{
          flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          <span style={{
            color: 'var(--text-primary)', fontWeight: 500,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{item.name}</span>
          {depHint && (
            <span style={{
              fontSize: 10, fontWeight: 400, color: 'var(--text-muted)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{depHint}</span>
          )}
        </span>
      )}

      {/* Spec approval gate */}
      {isSpec && (
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 3,
          color: specApproved ? 'var(--st-success, #22A06B)' : 'var(--text-muted)',
          fontSize: 11, fontWeight: 600,
        }} title={specApproved ? 'Approved — gate satisfied' : 'Awaiting approval'}>
          {specApproved && <Icon name="check" size={10} sw={2.4}/>}
          on approval
        </span>
      )}

      {/* Permit milestone stage pill */}
      {isPermitMilestone && item.permitStage && (
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '2px 7px', borderRadius: 'var(--r-sm)',
          background: stageMeta.bg, color: stageMeta.tx,
          fontFamily: 'var(--font-mono)', fontSize: 9.5, fontWeight: 700,
          letterSpacing: 0.5, textTransform: 'uppercase', flexShrink: 0,
        }}>
          <span style={{
            width: 5, height: 5, borderRadius: '50%', flexShrink: 0,
            background: stageMeta.dot,
          }} />
          {stageMeta.label}
        </span>
      )}

      <DepTypeBadge value={depType || 'FS'} onChange={onChangeType} canEdit={canEdit}/>
      <LeadLagInput value={lag} onChange={onChangeLag} canEdit={canEdit}/>
      {canEdit && (
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove dependency"
          style={{
            width: 18, height: 18, minHeight: 0, minWidth: 0, padding: 0, borderRadius: '50%',
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'var(--text-muted)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Icon name="x" size={10} sw={2.4}/>
        </button>
      )}
    </div>

    {/* ── Hover preview card ── */}
    {hoverOpen && (
      <div
        onMouseEnter={() => {
          if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null }
        }}
        onMouseLeave={scheduleHide}
        style={{
          position: 'fixed',
          top: hoverPos.top,
          left: hoverPos.left,
          minWidth: Math.max(hoverPos.width, 200),
          maxWidth: 320,
          zIndex: 1001,
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-strong)',
          borderRadius: 'var(--r-md)',
          boxShadow: '0 12px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)',
          padding: '10px 12px',
          display: 'flex', flexDirection: 'column', gap: 6,
          fontFamily: 'var(--font-sans)',
          pointerEvents: 'auto',
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.3, wordBreak: 'break-word' }}>
          {item.name}
        </div>
        {item.status && (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: statusDot(item.status), flexShrink: 0 }} />
            <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{statusLabel(item.status)}</span>
          </div>
        )}
        {isSpec && item.vendor && (
          <div style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
            <span style={{ color: 'var(--text-muted)', marginRight: 4 }}>Vendor</span>{item.vendor}
          </div>
        )}
        {item.dueDate && (
          <div style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
            <span style={{ color: 'var(--text-muted)', marginRight: 4 }}>Due</span>
            {formatDateWithOptions(new Date(item.dueDate), { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
        )}
        {canOpen && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 3 }}>
            <Icon name="chevR" size={9} sw={2} />
            Click to open
          </div>
        )}
      </div>
    )}
    </>
  )
}

// ─── DependencyPicker ─────────────────────────────────────────────────

/** Internal value shape (a dep record with item embedded) */
export interface DepValue {
  id: string
  /** Embedded display data keeps an existing dependency visible when the
   *  current surface's (often intentionally scoped) catalogue omits it. */
  name?: string
  type: 'task' | 'spec' | 'permit_milestone' | 'phase'
  status?: string
  depType?: 'FS' | 'SS' | 'FF' | 'SF'
  lag?: number
  /** PERMITS-REDESIGN-SURFACE-SWEEP — set when type === 'permit_milestone' */
  permitStage?: string
  /** The dependency row's own id (app_task_dependencies.id) — set by
   *  useTaskPeekDependencies, used to target the delete-dependency endpoint. */
  _depId?: number
}

export interface DependencyPickerProps {
  value?: DepValue[]
  onChange?: (v: DepValue[]) => void
  canEdit: boolean
  onOpenSpec?: ((id: string) => void) | null
  depItems?: DepItem[]
  /** TASK-DETAIL-PEEK-DEP-HOVER-PREVIEW — opens stacked peek for task deps */
  onOpenTask?: ((id: string) => void) | null
  /** #2798 — DELETEs the dependency row server-side. When set and a row has
   *  a `_depId`, remove uses this instead of the local-only onChange filter
   *  so predecessor removal persists (mirrors the Blocking-list remove). */
  onRemoveDependency?: (depId: number) => void
}

export function DependencyPicker({
  value = [],
  onChange,
  canEdit,
  onOpenSpec = null,
  depItems = [],
  onOpenTask = null,
  onRemoveDependency,
}: DependencyPickerProps) {
  const [query, setQuery] = React.useState('')

  // Phases are pickable dependency targets same as tasks (#2801) — merged
  // into one list, distinguished by the 'phase' badge in DependencyRow.
  const taskItems = depItems.filter(d => d.type === 'task' || d.type === 'phase')
  const specItems = depItems.filter(d => d.type === 'spec')

  const taskMatches = taskItems.filter(t => {
    if (!query) return true
    const q = query.toLowerCase()
    return t.name.toLowerCase().includes(q)
      || (t.project || '').toLowerCase().includes(q)
      || (t.phase || '').toLowerCase().includes(q)
      || (t.room || '').toLowerCase().includes(q)
  })
  const specMatches = specItems.filter(s =>
    !query || s.name.toLowerCase().includes(query.toLowerCase()))

  const itemById = (id: string, type: DepValue['type']): DepItem | undefined =>
    depItems.find(d => d.id === id && d.type === type)

  const itemForValue = (v: DepValue): DepItem => {
    const catalogueItem = itemById(v.id, v.type)
    if (catalogueItem) return {
      ...catalogueItem,
      name: v.name ?? catalogueItem.name,
      status: v.status ?? catalogueItem.status,
      permitStage: v.permitStage ?? catalogueItem.permitStage,
    }
    return { id: v.id, name: v.name ?? v.id, type: v.type, status: v.status, permitStage: v.permitStage }
  }

  const has = (id: string, type: DepValue['type']) =>
    value.some(v => v.id === id && v.type === type)

  const updateDep = (id: string, type: DepValue['type'], patch: Partial<DepValue>) => {
    onChange?.(value.map(v =>
      (v.id === id && v.type === type) ? { ...v, ...patch } : v))
  }

  if (!canEdit) {
    if (value.length === 0) return <PickerStatic placeholder label="No dependencies"/>
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '6px 0' }}>
        {value.map(v => {
          const item = itemForValue(v)
          return (
            <DependencyRow key={`${v.type}:${v.id}`}
              item={item}
              depType={v.depType}
              lag={v.lag}
              onOpenSpec={onOpenSpec ?? undefined}
              onOpenTask={onOpenTask ?? undefined}
              canEdit={false}/>
          )
        })}
      </div>
    )
  }

  return (
    <div>
      {value.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
          {value.map(v => {
            const item = itemForValue(v)
            return (
              <DependencyRow key={`${v.type}:${v.id}`}
                item={item}
                depType={v.depType}
                lag={v.lag}
                onOpenSpec={onOpenSpec ?? undefined}
                onOpenTask={onOpenTask ?? undefined}
                canEdit
                onChangeType={(dt) => updateDep(v.id, v.type, { depType: dt as DepValue['depType'] })}
                onChangeLag={(l) => updateDep(v.id, v.type, { lag: l })}
                onRemove={() => {
                  // Fire the shared DELETE (persists on surfaces whose onFieldSave
                  // doesn't special-case 'dependencies') AND the local onChange
                  // (preserves ProjectSchedulePage/TasksPage's own diff-based
                  // add/update/remove + Gantt refresh). Both target the same
                  // row id, so a resulting duplicate DELETE is idempotent.
                  if (onRemoveDependency && v._depId != null) onRemoveDependency(v._depId)
                  onChange?.(value.filter(x => !(x.id === v.id && x.type === v.type)))
                }}
              />
            )
          })}
        </div>
      )}
      <Popover
        side="bottom" align="start" width={340}
        trigger={<PickerTrigger label="+ Add dependency…" placeholder/>}
      >
        <OptionList
          search={<PopoverSearch value={query} onChange={setQuery} placeholder="Search tasks + specs…"/>}
        >
          {taskMatches.length > 0 && (
            <div style={{
              padding: '6px 10px 4px',
              fontFamily: 'var(--font-sans)', fontSize: 10.5, fontWeight: 700,
              letterSpacing: 0.6, textTransform: 'uppercase',
              color: 'var(--text-muted)',
            }}>Tasks</div>
          )}
          {taskMatches.map(t => (
            <OptionRow key={`t:${t.id}`}
              dot={t.status ? `var(--status-${t.status.replace(/_/g, '-')}-dot)` : undefined}
              label={t.name}
              hint={[t.project, t.phase, t.room].filter(Boolean).join(' · ') || undefined}
              checked={has(t.id, t.type)}
              onClick={(e) => {
                e?.stopPropagation?.()
                const next = has(t.id, t.type)
                  ? value.filter(v => !(v.id === t.id && v.type === t.type))
                  : [...value, { id: t.id, name: t.name, status: t.status, type: t.type, depType: 'FS' as const, lag: 0 }]
                onChange?.(next)
              }}/>
          ))}
          {specMatches.length > 0 && (
            <div style={{
              padding: '8px 10px 4px',
              fontFamily: 'var(--font-sans)', fontSize: 10.5, fontWeight: 700,
              letterSpacing: 0.6, textTransform: 'uppercase',
              color: 'var(--text-muted)',
            }}>Finish Schedule</div>
          )}
          {specMatches.map(s => {
            // Build a minimal SpecOption for SpecRow display
            const specOpt: SpecOption = { id: s.id, name: s.name, status: s.status }
            return (
              <SpecRow key={`s:${s.id}`}
                s={specOpt}
                checked={has(s.id, 'spec')}
                onClick={(e) => {
                  e?.stopPropagation?.()
                  const next = has(s.id, 'spec')
                    ? value.filter(v => !(v.id === s.id && v.type === 'spec'))
                    : [...value, { id: s.id, name: s.name, status: s.status, type: 'spec' as const, depType: 'FS' as const, lag: 0 }]
                  onChange?.(next)
                }}/>
            )
          })}
        </OptionList>
      </Popover>
    </div>
  )
}

// ─── FSBridgeBlock ────────────────────────────────────────────────────

function FSMeta({ label, value, tone = null }: { label: string; value: React.ReactNode; tone?: 'danger' | null }) {
  return (
    <div>
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 9.5, fontWeight: 700,
        letterSpacing: 0.6, textTransform: 'uppercase',
        color: 'var(--text-muted)', marginBottom: 3,
      }}>{label}</div>
      <div style={{
        fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600,
        color: tone === 'danger' ? 'var(--st-error)' : 'var(--text-primary)',
      }}>{value}</div>
    </div>
  )
}

export interface FSBridgeBlockProps {
  spec?: SpecOption | null
  onOpenInFS?: () => void
  canEdit?: boolean
  onUnlink?: () => void
}

export function FSBridgeBlock({ spec, onOpenInFS, canEdit = true, onUnlink }: FSBridgeBlockProps) {
  if (!spec) return null
  const s = spec
  const selected = s.selectedPrice ?? s.selected
  const over = !!(s.allowance && selected != null && selected > s.allowance)
  const tint = SPEC_TYPE_TINT[s.specType || ''] || 'var(--text-muted)'
  const fmt$ = (n?: number | null) => n == null
    ? '—'
    : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

  return (
    <div style={{
      position: 'relative',
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--r-md)',
      padding: 14,
      marginTop: 4,
    }}>
      {/* Left accent stripe in spec-type tint */}
      <span aria-hidden="true" style={{
        position: 'absolute', left: 0, top: 14, bottom: 14, width: 3,
        background: tint, borderRadius: 2,
      }}/>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{
          padding: '2px 7px', borderRadius: 4,
          background: 'color-mix(in srgb, var(--brand-gold) 18%, transparent)',
          color: 'var(--brand-gold)',
          fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700,
          letterSpacing: 0.7, textTransform: 'uppercase',
        }}>Finish Schedule</span>
        <span style={{
          fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)',
        }}>
          {[s.id, s.room, s.specType].filter(Boolean).join(' · ')}
        </span>
        <div style={{ flex: 1 }}/>
        {onOpenInFS && (
          <button
            type="button"
            onClick={onOpenInFS}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: 'var(--brand-gold)',
              fontFamily: 'var(--font-sans)', fontSize: 12, fontWeight: 600,
              padding: '2px 6px', borderRadius: 4,
            }}
          >
            Open in FS
            <svg width="10" height="10" viewBox="0 0 14 14" fill="none">
              <path d="M9 1h4v4M5 13H1V9M13 1l-5 5M1 13l5-5"
                stroke="currentColor" strokeWidth="1.6"
                strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <SwatchStack swatches={s.swatches || []} size={20}/>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            fontFamily: 'var(--font-sans)', fontSize: 14.5, fontWeight: 700,
            color: 'var(--text-primary)', letterSpacing: -0.1,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{s.name}</div>
          <div style={{
            fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--text-muted)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{s.vendor || (s.placeholder ? 'Placeholder — no vendor yet' : '—')}</div>
        </div>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8,
        marginBottom: canEdit ? 10 : 0,
      }}>
        <FSMeta label="Status" value={
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontFamily: 'var(--font-sans)', fontSize: 12, fontWeight: 600,
            color: 'var(--text-primary)', textTransform: 'capitalize',
          }}>
            <span style={{
              width: 7, height: 7, borderRadius: '50%',
              background: SPEC_STATUS_DOT[s.status || ''] || 'var(--text-muted)',
            }}/>
            {s.status || '—'}
          </span>
        }/>
        <FSMeta label="Allowance" value={fmt$(s.allowance)}/>
        <FSMeta
          label="Selected"
          tone={over ? 'danger' : null}
          value={selected == null ? '—' : (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {fmt$(selected)}
              {over && (
                <span style={{
                  padding: '1px 4px', borderRadius: 3,
                  background: 'var(--st-error)', color: 'var(--st-error-ink)',
                  fontFamily: 'var(--font-mono)', fontSize: 9, fontWeight: 700,
                }}>OVER</span>
              )}
            </span>
          )}
        />
      </div>

      {canEdit && onUnlink && (
        <div style={{
          paddingTop: 10, borderTop: '1px solid var(--border)',
          display: 'flex', alignItems: 'center',
        }}>
          <FooterAction icon="x" label="Unlink spec" tone="muted" onClick={onUnlink}/>
        </div>
      )}
    </div>
  )
}

// ─── DecisionOptionCard ───────────────────────────────────────────────

const inlineMutedInput: React.CSSProperties = {
  background: 'transparent', border: 'none', outline: 'none',
  padding: 0, color: 'var(--text-muted)',
  fontFamily: 'var(--font-sans)', fontSize: 11.5, fontWeight: 600,
  width: 88,
}

function badgeStyle(color: string): React.CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center',
    padding: '2px 6px', borderRadius: 3,
    background: `color-mix(in srgb, ${color} 18%, transparent)`,
    color,
    fontFamily: 'var(--font-mono)', fontSize: 9.5, fontWeight: 700,
    letterSpacing: 0.6, textTransform: 'uppercase',
  }
}

export interface DecisionOptionCardProps {
  option: DecisionOption
  idx: number
  canEdit: boolean
  showPricing?: boolean
  onChange?: (v: DecisionOption) => void
  onRemove?: () => void
  onMakeBaseline?: (idx: number) => void
}

export function DecisionOptionCard({
  option, idx, canEdit, showPricing = true,
  onChange, onRemove, onMakeBaseline,
}: DecisionOptionCardProps) {
  const letter = String.fromCharCode(65 + idx)
  const fmt$ = (n?: number | null) => n == null
    ? '—'
    : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

  const menuItems: DropdownMenuItem[] = [
    {
      label: option.isBaseline ? 'Unset baseline' : 'Make baseline',
      icon: 'check',
      onSelect: () => onMakeBaseline?.(idx),
    },
    {
      label: option.proposedByClient ? 'Mark as company option' : 'Mark as client-proposed',
      icon: 'msg',
      onSelect: () => onChange?.({ ...option, proposedByClient: !option.proposedByClient }),
    },
    { separator: true },
    { label: 'Duplicate', icon: 'layers', onSelect: () => {} },
    { label: 'Remove', icon: 'trash', tone: 'danger', onSelect: onRemove },
  ]

  return (
    <div style={{
      position: 'relative',
      background: option.isBaseline
        ? 'color-mix(in srgb, var(--brand-gold) 6%, var(--bg-surface))'
        : 'var(--bg-surface)',
      border: option.isBaseline
        ? '1px solid color-mix(in srgb, var(--brand-gold) 45%, var(--border))'
        : '1px solid var(--border)',
      borderRadius: 'var(--r-md)',
      padding: 10,
      display: 'grid',
      gridTemplateColumns: '60px 1fr auto',
      gap: 10,
    }}>
      {/* Letter + image slot */}
      <div style={{
        position: 'relative',
        width: 60, height: 60, borderRadius: 'var(--r-sm)',
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        overflow: 'hidden',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {option.imageUrl ? (
          <img src={option.imageUrl} alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}/>
        ) : (
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: 18, fontWeight: 700,
            color: 'var(--text-muted)',
          }}>{letter}</span>
        )}
        <span style={{
          position: 'absolute', top: 3, left: 3,
          padding: '0 5px', borderRadius: 3,
          background: 'rgba(0,0,0,0.55)', color: 'white',
          fontFamily: 'var(--font-mono)', fontSize: 9.5, fontWeight: 700,
          letterSpacing: 0.4,
        }}>{letter}</span>
      </div>

      {/* Main body */}
      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {canEdit ? (
          <input
            defaultValue={option.title || ''}
            onBlur={(e) => onChange?.({ ...option, title: e.target.value })}
            placeholder="Option name (e.g. Anatolia Calacatta 12×24)"
            style={{
              width: '100%', background: 'transparent', border: 'none', outline: 'none',
              padding: 0,
              fontFamily: 'var(--font-sans)', fontSize: 13.5, fontWeight: 700,
              color: 'var(--text-primary)',
            }}
          />
        ) : (
          <div style={{
            fontFamily: 'var(--font-sans)', fontSize: 13.5, fontWeight: 700,
            color: 'var(--text-primary)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {option.title || <span style={{ color: 'var(--text-dim)' }}>(Untitled)</span>}
          </div>
        )}

        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
          fontFamily: 'var(--font-sans)', fontSize: 11.5,
          color: 'var(--text-muted)',
        }}>
          {canEdit ? (
            <input
              defaultValue={option.brand || ''}
              onBlur={(e) => onChange?.({ ...option, brand: e.target.value })}
              placeholder="Brand"
              style={inlineMutedInput}
            />
          ) : (
            option.brand && <span>{option.brand}</span>
          )}
          {(option.brand || canEdit) && (option.sku || canEdit) && <span>·</span>}
          {canEdit ? (
            <input
              defaultValue={option.sku || ''}
              onBlur={(e) => onChange?.({ ...option, sku: e.target.value })}
              placeholder="SKU"
              style={{ ...inlineMutedInput, fontFamily: 'var(--font-mono)' }}
            />
          ) : (
            option.sku && (
              <span style={{ fontFamily: 'var(--font-mono)' }}>{option.sku}</span>
            )
          )}
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          marginTop: 2,
        }}>
          {option.isBaseline && (
            <span style={badgeStyle('var(--brand-gold)')}>★ Baseline</span>
          )}
          {option.proposedByClient && (
            <span style={badgeStyle('var(--status-in-review-dot, #1F8FFF)')}>Proposed by client</span>
          )}
          {option.specSheetUrl && (
            <a
              href={option.specSheetUrl}
              onClick={(e) => e.preventDefault()}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                fontFamily: 'var(--font-sans)', fontSize: 11, fontWeight: 600,
                color: 'var(--brand-gold)', textDecoration: 'none',
              }}
            >
              <Icon name="paperclip" size={10} sw={1.8}/>
              Spec sheet
            </a>
          )}
        </div>
      </div>

      {/* Right rail — price + qty + actions */}
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4,
        minWidth: 70,
      }}>
        {showPricing && (
          canEdit ? (
            <input
              defaultValue={option.price ?? ''}
              type="number"
              onBlur={(e) => onChange?.({
                ...option,
                price: e.target.value === '' ? null : +e.target.value,
              })}
              placeholder="0"
              style={{
                width: 80, background: 'var(--bg-elevated)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-sm)',
                padding: '4px 6px',
                textAlign: 'right',
                fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700,
                color: 'var(--text-primary)',
                outline: 'none',
              }}
            />
          ) : (
            <span style={{
              fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 700,
              color: 'var(--text-primary)',
            }}>{fmt$(option.price)}</span>
          )
        )}
        {showPricing && ((option.quantity ?? 0) > 1 || canEdit) && (
          <span style={{
            fontFamily: 'var(--font-sans)', fontSize: 11,
            color: 'var(--text-muted)',
          }}>× {option.quantity} {option.unit}</span>
        )}
        {canEdit && (
          <DropdownMenu
            side="bottom" align="end" width={180}
            trigger={
              <button
                type="button"
                aria-label="Option actions"
                style={{
                  width: 22, height: 22, padding: 0, marginTop: 4,
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: 'var(--text-muted)', borderRadius: 'var(--r-sm)',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Icon name="more" size={13} sw={2}/>
              </button>
            }
            items={menuItems}
          />
        )}
      </div>
    </div>
  )
}

// ─── DecisionOptionsEditor ────────────────────────────────────────────

export interface DecisionOptionsEditorProps {
  value?: DecisionOption[]
  onChange?: (v: DecisionOption[]) => void
  canEdit: boolean
  showPricing?: boolean
  mode?: 'single' | 'multiple'
}

export function DecisionOptionsEditor({
  value = [],
  onChange,
  canEdit,
  showPricing = true,
  mode = 'single',
}: DecisionOptionsEditorProps) {
  const onMakeBaseline = (idx: number) => {
    const next = value.map((o, i) => ({ ...o, isBaseline: i === idx ? !o.isBaseline : false }))
    onChange?.(next)
  }

  if (!canEdit && value.length === 0) {
    return <PickerStatic placeholder label="No options listed"/>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {value.map((opt, i) => (
        <DecisionOptionCard
          key={opt.id || i}
          option={opt}
          idx={i}
          canEdit={canEdit}
          showPricing={showPricing}
          onChange={(next) => onChange?.(value.map((o, j) => j === i ? { ...next, id: o.id } : o))}
          onRemove={() => onChange?.(value.filter((_, j) => j !== i))}
          onMakeBaseline={onMakeBaseline}
        />
      ))}
      {canEdit && (
        <button
          type="button"
          onClick={() => {
            onChange?.([
              ...value,
              {
                id: `o-${Date.now()}`,
                title: '', brand: '', sku: '',
                price: null, quantity: 1, unit: 'ea',
                imageUrl: null, productUrl: '', specSheetUrl: '', notes: '',
                isBaseline: value.length === 0,
                proposedByClient: false,
              },
            ])
          }}
          style={{
            alignSelf: 'flex-start',
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 12px', borderRadius: 'var(--r-sm)',
            background: 'transparent', border: '1.5px dashed var(--border-strong)',
            cursor: 'pointer',
            color: 'var(--brand-gold)',
            fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600,
          }}
        >
          <Icon name="plus" size={12} sw={2}/>
          Add option
          {mode === 'single' && value.length === 0 && (
            <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>
              · client picks one
            </span>
          )}
        </button>
      )}
      {canEdit && (
        <div style={{
          fontFamily: 'var(--font-sans)', fontSize: 11,
          color: 'var(--text-muted)', marginTop: 2,
        }}>
          Baseline is the default option. Client can propose alternatives from the portal.
        </div>
      )}
    </div>
  )
}

// ─── DecisionStatusPicker ─────────────────────────────────────────────

export interface DecisionStatusPickerProps {
  value?: string
  onChange?: (v: string) => void
  canEdit: boolean
  /** Status ids that are greyed out + not clickable (collaborator role) */
  disabledIds?: string[]
}

export function DecisionStatusPicker({ value = 'draft', onChange, canEdit, disabledIds = [] }: DecisionStatusPickerProps) {
  const cur = DECISION_STATUS.find(s => s.id === value) || DECISION_STATUS[0]
  if (!canEdit) return <PickerStatic dot={cur.dot} label={cur.label}/>
  return (
    <Popover
      side="bottom" align="start" width={220}
      trigger={<PickerTrigger dot={cur.dot} label={cur.label}/>}
      closeOnSelect
    >
      <OptionList>
        {DECISION_STATUS.map(s => {
          const isDisabled = disabledIds.includes(s.id)
          if (isDisabled) {
            return (
              <div key={s.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 10px', borderRadius: 'var(--r-sm)',
                opacity: 0.38, cursor: 'not-allowed',
                fontFamily: 'var(--font-sans)', fontSize: 13,
                color: 'var(--text-primary)',
              }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.dot, flexShrink: 0 }}/>
                <span style={{ flex: 1, fontWeight: 500 }}>{s.label}</span>
              </div>
            )
          }
          return (
            <OptionRow key={s.id} dot={s.dot} label={s.label}
              checked={s.id === value}
              onClick={() => onChange?.(s.id)}/>
          )
        })}
      </OptionList>
    </Popover>
  )
}

// ─── SelectionModePicker ──────────────────────────────────────────────

export interface SelectionModePickerProps {
  value?: string
  onChange?: (v: string) => void
  canEdit: boolean
}

export function SelectionModePicker({ value = 'single', onChange, canEdit }: SelectionModePickerProps) {
  const cur = SELECTION_MODES.find(m => m.id === value) || SELECTION_MODES[0]
  if (!canEdit) return <PickerStatic label={cur.label}/>
  return (
    <Popover
      side="bottom" align="start" width={240}
      trigger={<PickerTrigger label={cur.label}/>}
      closeOnSelect
    >
      <OptionList>
        {SELECTION_MODES.map(m => (
          <OptionRow key={m.id} label={m.label} hint={m.hint}
            checked={m.id === value}
            onClick={() => onChange?.(m.id)}/>
        ))}
      </OptionList>
    </Popover>
  )
}

// ─── CloseModePicker ──────────────────────────────────────────────────

export interface CloseModePickerProps {
  value?: string
  onChange?: (v: string) => void
  canEdit: boolean
}

export function CloseModePicker({ value = 'manual', onChange, canEdit }: CloseModePickerProps) {
  const cur = CLOSE_MODES.find(m => m.id === value) || CLOSE_MODES[0]
  if (!canEdit) return <PickerStatic label={cur.label}/>
  return (
    <Popover
      side="bottom" align="start" width={260}
      trigger={<PickerTrigger label={cur.label}/>}
      closeOnSelect
    >
      <OptionList>
        {CLOSE_MODES.map(m => (
          <OptionRow key={m.id} label={m.label} hint={m.hint}
            checked={m.id === value}
            onClick={() => onChange?.(m.id)}/>
        ))}
      </OptionList>
    </Popover>
  )
}

// ─── SubKindPicker — segmented 2-option control ───────────────────────

export interface SubKindPickerProps {
  value?: string
  onChange?: (v: string) => void
  canEdit: boolean
}

export function SubKindPicker({ value = 'selection', onChange, canEdit }: SubKindPickerProps) {
  if (!canEdit) {
    const cur = DECISION_SUB_KINDS.find(s => s.id === value)
    return <PickerStatic label={cur?.label || '—'}/>
  }
  return (
    <div style={{
      display: 'inline-flex', padding: 2, gap: 2,
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--r-md)',
    }}>
      {DECISION_SUB_KINDS.map(s => {
        const on = s.id === value
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => onChange?.(s.id)}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
              padding: '6px 12px', borderRadius: 'calc(var(--r-md) - 2px)',
              background: on ? 'var(--brand-gold)' : 'transparent',
              border: 'none', cursor: 'pointer',
              color: on ? 'var(--brand-gold-ink, #1a1000)' : 'var(--text-secondary)',
              fontFamily: 'var(--font-sans)', textAlign: 'left',
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.1 }}>{s.label}</span>
            <span style={{
              fontSize: 10.5, fontWeight: 500, marginTop: 2,
              opacity: 0.8, lineHeight: 1.1,
            }}>{s.hint}</span>
          </button>
        )
      })}
    </div>
  )
}

// ─── FromSpecMaterialRow ──────────────────────────────────────────────

export interface FromSpecMaterialRowProps {
  specId: string
  optionTitle?: string
  vendor?: string
  price?: number | null
  specType?: string
  onOpenSpec?: (id: string) => void
  canEdit?: boolean
}

export function FromSpecMaterialRow({
  specId,
  optionTitle,
  vendor,
  price,
  specType,
  onOpenSpec,
}: FromSpecMaterialRowProps) {
  const tint = SPEC_TYPE_TINT[specType || ''] || 'var(--text-muted)'
  const fmt$ = (n?: number | null) => n == null
    ? '—'
    : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '6px 1fr auto auto',
      gap: 10, alignItems: 'center',
      padding: '8px 10px',
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--r-sm)',
    }}>
      <span aria-hidden="true" style={{
        width: 4, height: 28, borderRadius: 2,
        background: tint, alignSelf: 'center',
      }}/>
      <div style={{ minWidth: 0 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600,
          color: 'var(--text-primary)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          <svg width="11" height="11" viewBox="0 0 14 14" fill="none"
            style={{ flexShrink: 0, color: 'var(--text-muted)' }}>
            <path d="M4 7V4a3 3 0 0 1 6 0v3M3 7h8v6H3z"
              stroke="currentColor" strokeWidth="1.6"
              strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {optionTitle || specId}
        </div>
        <div style={{
          fontFamily: 'var(--font-sans)', fontSize: 11.5,
          color: 'var(--text-muted)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{vendor || '—'}</div>
      </div>
      <div style={{
        textAlign: 'right',
        fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700,
        color: 'var(--text-primary)',
      }}>{fmt$(price)}</div>
      <button
        type="button"
        onClick={() => onOpenSpec?.(specId)}
        title="Open the source Decision in Finish Schedule"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '3px 6px', borderRadius: 4,
          background: 'color-mix(in srgb, var(--brand-gold) 18%, transparent)',
          color: 'var(--brand-gold)',
          border: 'none', cursor: onOpenSpec ? 'pointer' : 'default',
          fontFamily: 'var(--font-mono)', fontSize: 9.5, fontWeight: 700,
          letterSpacing: 0.6, textTransform: 'uppercase',
        }}
      >
        from spec
        <Icon name="chevR" size={9} sw={2}/>
      </button>
    </div>
  )
}
